import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import { Button } from '@treezor/design-system';

import { getPermsGroup } from '@/features/cards/components/bulkCardCreation/helpers';
import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import { setBulkCardCreationStep } from '@/store/actions/bulkCardCreations.actions';

import { IToggleCardOptions } from '@/features/cards/store/models/card.model';

import { Switch } from '@/ui-kit/switch/Switch';

export const PermsGroup = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const activeStep = useAppSelector((state) => state.bulkCardCreation.currentStep);
  const bulkCardCreationDraft = useAppSelector((state) => state.bulkCardCreation.bulkCardCreationDraft);
  const dashboardConfig = useDashboardConfig();

  const { handleSubmit, watch, setValue } = useForm<IToggleCardOptions>({
    defaultValues: getPermsGroup(bulkCardCreationDraft?.defaultPermsGroup),
  });

  const permOptions = dashboardConfig.settings?.bulkCards?.permsGroup;

  const doToggleOptions = useCallback(
    () => (options: IToggleCardOptions) => {
      if (
        !options ||
        options.nfc === undefined ||
        options.atm === undefined ||
        options.online === undefined ||
        options.foreign === undefined ||
        options.pinUnverified === undefined
      ) {
        return;
      }
      const index =
        options.nfc * 8 + options.atm * 4 + options.online * 2 + options.foreign + options.pinUnverified + 1;
      const permsgroup: string = 'TRZ-CU-0' + (index < 10 ? '0' : '') + index;
      dispatch(setBulkCardCreationStep(activeStep + 1, { defaultPermsGroup: permsgroup }));
    },
    [dispatch, activeStep],
  );

  const isPinUnverifiedEnabled =
    watch('online') === 1 && watch('atm') === 1 && watch('nfc') === 1 && watch('foreign') === 1;

  useEffect(() => {
    if (!isPinUnverifiedEnabled && watch('pinUnverified') === 1) {
      setValue('pinUnverified', 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPinUnverifiedEnabled]);

  return (
    <>
      <h5 className="typo-h5-bold text-secondary">{t('pages.administration.bulkCards.permsGroup.title')}</h5>
      <div className="flex flex-col gap-2 mt-7 w-[250px]">
        {permOptions.map((permOption, index) => (
          <Switch
            key={`perms-group-${index}-${permOption}`}
            label={t(`pages.administration.bulkCards.permsGroup.${permOption.toUpperCase()}`)}
            hint={t(`pages.administration.bulkCards.permsGroup.description.${permOption}`)}
            isDisabled={permOption === 'pinUnverified' ? !isPinUnverifiedEnabled : false}
            isEnabled={watch(permOption) === 1}
            handleChange={(value) => setValue(permOption, value === true ? 1 : 0)}
          />
        ))}
        <div className="flex items-center justify-center mt-3">
          <Button type="button" id="bulk-cards-perms-groups-btn" onClick={handleSubmit(doToggleOptions())}>
            {t('pages.administration.bulkCards.permsGroup.permsBtn')}
          </Button>
        </div>
      </div>
    </>
  );
};
